// =====================================================================
// AnalyticsContext, single source of truth for product events.
// ---------------------------------------------------------------------
// Wraps PostHog so the rest of the app never imports posthog-js
// directly. Exposes:
//
//   const { capture, identify, reset, optIn, optOut } = useAnalytics();
//
//   capture("Clicked Upgrade Infrastructure", { plan: "payg" });
//   identify(user.id, { email, role });
//
// Behaviour:
//   • PostHog itself is dynamic-imported on mount (see lib/posthog.js),
//     so its ~190 KB chunk never blocks the landing-page paint. Until
//     the boot promise resolves, capture/identify/etc. are no-ops.
//   • Identifies the user on every auth state change (Supabase user.id
//     becomes the PostHog distinct_id, so anonymous → signed-up users
//     are stitched together automatically).
//   • Fires "$pageview" on every route change.
//   • Calls posthog.reset() on sign-out so the next user gets a fresh
//     distinct_id.
// =====================================================================

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import { initPostHog } from "../lib/posthog.js";
import { useAuth } from "./AuthContext.jsx";

const AnalyticsContext = createContext(null);

export function AnalyticsProvider({ children }) {
  const { user, profile } = useAuth();
  const location = useLocation();
  const lastIdentified = useRef(null);
  // The booted PostHog instance, stored in state so route-change /
  // identify effects re-run once the dynamic import resolves.
  const [ph, setPh] = useState(null);

  // Boot PostHog once, fires the dynamic import, then stashes the
  // instance in state when ready. If PostHog is disabled (no key,
  // SSR, etc.) the promise resolves to null and `ph` stays null.
  useEffect(() => {
    let cancelled = false;
    initPostHog().then((instance) => {
      if (!cancelled) setPh(instance);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Identify on user / profile changes; reset on sign-out.
  useEffect(() => {
    if (!ph) return;
    if (user?.id) {
      if (lastIdentified.current !== user.id) {
        ph.identify(user.id, {
          email: profile?.email ?? user.email,
          role: profile?.role ?? "user",
          plan: profile?.plan ?? null,
          full_name: profile?.full_name ?? null,
        });
        lastIdentified.current = user.id;
      }
    } else if (lastIdentified.current) {
      ph.reset();
      lastIdentified.current = null;
    }
  }, [ph, user, profile]);

  // SPA pageviews, fire on every route change once PostHog is ready.
  useEffect(() => {
    if (!ph) return;
    ph.capture("$pageview", {
      $current_url: window.location.href,
      path: location.pathname,
    });
  }, [ph, location.pathname]);

  const capture = useCallback(
    (event, props = {}) => {
      if (!ph) return;
      ph.capture(event, props);
    },
    [ph]
  );

  const identify = useCallback(
    (id, props) => {
      if (!ph) return;
      ph.identify(id, props);
    },
    [ph]
  );

  const reset = useCallback(() => {
    if (!ph) return;
    ph.reset();
  }, [ph]);

  const optIn = useCallback(() => {
    if (!ph) return;
    ph.opt_in_capturing();
  }, [ph]);

  const optOut = useCallback(() => {
    if (!ph) return;
    ph.opt_out_capturing();
  }, [ph]);

  const value = useMemo(
    () => ({ capture, identify, reset, optIn, optOut }),
    [capture, identify, reset, optIn, optOut]
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics() {
  const ctx = useContext(AnalyticsContext);
  if (!ctx) {
    // Allow components outside the provider tree (e.g. during tests)
    // to call analytics safely, they just no-op.
    return {
      capture: () => {},
      identify: () => {},
      reset: () => {},
      optIn: () => {},
      optOut: () => {},
    };
  }
  return ctx;
}
